import { EventEmitter, Injectable, Output } from "@angular/core";
import { MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { firstValueFrom } from "rxjs";
import { PopupService } from "./popup.service";
import { AuthService } from "./auth.service";
import { Appointment } from "../models";
import { ApiService } from "./api.service";
import { AppointmentApiType } from "../constants";
import { UserClientService } from "./user-client.service";
import { PendingReviewModel } from "../models/review.model";
import type { LessonFeedbackDialogComponent } from "../standalone-components/review/lesson-feedback-dialog/lesson-feedback-dialog.component";

interface ActiveFeedback {
    type: AppointmentApiType;
    timer: number;
    appointment: Appointment;
}

interface LessonTutorData {
    forename: string;
    surname: string;
    image: string;
    studentId: string;
    tutorId: string;
}

interface PendingReviewGroup {
    ids: string[];
    tutorData: LessonTutorData;
}

@Injectable({
    providedIn: "root",
})
export class FeedbackFlowService {
    private activeFeedback: ActiveFeedback | null = null;
    private boundOnVisibilityChange: any;
    private isProcessingPendingReviews = false;

    @Output() onBookLesson = new EventEmitter<void>();
    @Output() onReviewSubmitted = new EventEmitter<void>();

    constructor(
        private popupService: PopupService,
        private authService: AuthService,
        private apiService: ApiService,
        private userClientService: UserClientService
    ) {
        this.boundOnVisibilityChange = this.onVisibilityChange.bind(this);
    }

    prepareFeedback(appointment: Appointment): void {
        this.activeFeedback = {
            type: appointment.type as AppointmentApiType,
            timer: new Date().getTime(),
            appointment: appointment
        };
        this.startListeningForVisibilityChange();
        if (appointment.type === AppointmentApiType.introduction) {
            const introData = {
                ...this.buildTutorData(appointment),
                endTime: appointment.end,
            };
            localStorage.setItem("introFeedbackDialog", JSON.stringify(introData));
        } else {
            const lessonData = {
                ...this.buildTutorData(appointment),
                endTime: appointment.end,
            };
            localStorage.setItem("lessonReviewDialog", JSON.stringify(lessonData));
        }
    }

    checkAndOpenPendingDialogs(): void {
        if (!this.authService.isStudent()) {
            return;
        }
        const isInLessonspace = location.href.includes("lessonspace");
        const introFeedbackDialog = localStorage?.getItem('introFeedbackDialog');
        const lessonReviewDialog = localStorage?.getItem('lessonReviewDialog');
        let openedOnlineDialog = false;
        if (introFeedbackDialog && !isInLessonspace) {
            const introData = JSON.parse(introFeedbackDialog);
            if (!introData.endTime || this.isSessionEnded(introData.endTime)) {
                this.openIntroReviewDialog(introData);
                openedOnlineDialog = true;
            }
        }
        if (lessonReviewDialog && !isInLessonspace) {
            const lessonData = JSON.parse(lessonReviewDialog);
            if (!lessonData.endTime || this.isSessionEnded(lessonData.endTime)) {
                this.openLessonReviewDialog(lessonData);
                openedOnlineDialog = true;
            }
        }
        if (!openedOnlineDialog && !isInLessonspace && this.hasPendingAtHomeReviews()) {
            this.openPendingAtHomeReviews();
        }
    }

    stopListeningForVisibilityChange(): void {
        document.removeEventListener("visibilitychange", this.boundOnVisibilityChange);
        this.activeFeedback = null;
    }

    private startListeningForVisibilityChange(): void {
        document.addEventListener("visibilitychange", this.boundOnVisibilityChange);
    }

    private buildTutorData(appointment: Appointment): any {
        return {
            forename: appointment.tutor.forename,
            surname: appointment.tutor.surname,
            image: appointment.tutor.profileImage,
            id: appointment.tutor.id,
            studentId: appointment.student?.id,
            tutorId: appointment.tutor.id,
            tutorCategory: appointment.tutor.tutorCategory,
        };
    }

    private onVisibilityChange(event: Event): void {
        if (!this.activeFeedback || document.visibilityState !== "visible") {
            return;
        }
        const shouldTrigger = this.isSessionEnded(this.activeFeedback.appointment.end);
        if (shouldTrigger) {
            if (!localStorage.getItem(this.activeFeedback.type === AppointmentApiType.introduction ? "introFeedbackDialog" : "lessonReviewDialog")) {
                this.stopListeningForVisibilityChange();
                return;
            }
            const tutorData = this.buildTutorData(this.activeFeedback.appointment);
            if (this.activeFeedback.type === AppointmentApiType.introduction) {
                this.openIntroReviewDialog(tutorData);
            } else {
                this.openLessonReviewDialog(tutorData);
            }
        }
    }

    async openIntroReviewDialog(tutorData) {
        localStorage.removeItem("introFeedbackDialog");
        const component = await import('../standalone-components/booking/intro-feedback-dialog/intro-feedback-dialog.component');
        const dialogComp = this.popupService.openResponsiveDialog(component.IntroFeedbackDialogComponent).componentInstance;
        dialogComp.tutorBasicData = tutorData;
        dialogComp.onBookLesson.subscribe(() => {
            this.onBookLesson.emit();
        });
        this.stopListeningForVisibilityChange();
    }

    async openLessonReviewDialog(tutorData) {
        localStorage.removeItem("lessonReviewDialog");
        await this.openLessonDialog(tutorData);
        this.stopListeningForVisibilityChange();
    }

    private async openLessonDialog(
        tutorData: LessonTutorData,
        config?: MatDialogConfig
    ): Promise<MatDialogRef<LessonFeedbackDialogComponent>> {
        const component = await import('../standalone-components/review/lesson-feedback-dialog/lesson-feedback-dialog.component');
        const dialogRef = this.popupService.openResponsiveDialog(component.LessonFeedbackDialogComponent, config);
        dialogRef.componentInstance.tutorBasicData = tutorData;
        return dialogRef;
    }

    private hasPendingAtHomeReviews(): boolean {
        const flag = this.authService.userDetails?.studentDetails?.hasPendingReviews;
        return flag === true || flag === 'true';
    }

    private async openPendingAtHomeReviews(): Promise<void> {
        if (this.isProcessingPendingReviews) {
            return;
        }
        this.isProcessingPendingReviews = true;
        try {
            const response = await firstValueFrom(this.userClientService.getPendingReviews(), { defaultValue: null });
            const groups = this.groupPendingReviewsByTutor(response?.reviews ?? []);
            if (groups.length === 0) {
                return;
            }
            for (const group of groups) {
                await this.showPendingReviewGroup(group);
            }
            this.clearPendingReviewsFlag();
        } catch {
            // Silent by design: a failed fetch means the popup is simply not shown this time.
        } finally {
            this.isProcessingPendingReviews = false;
        }
    }

    private groupPendingReviewsByTutor(reviews: PendingReviewModel[]): PendingReviewGroup[] {
        const groups = new Map<string, PendingReviewGroup>();
        for (const review of reviews) {
            const existing = groups.get(review.tutorId);
            if (existing) {
                existing.ids.push(review.id);
                continue;
            }
            groups.set(review.tutorId, {
                ids: [review.id],
                tutorData: {
                    forename: review.tutorForename,
                    surname: review.tutorSurname,
                    image: review.tutorProfileImage,
                    studentId: review.studentId,
                    tutorId: review.tutorId,
                }
            });
        }
        return Array.from(groups.values());
    }

    private async showPendingReviewGroup(group: PendingReviewGroup): Promise<void> {
        const dialogRef = await this.openLessonDialog(group.tutorData, { closeOnNavigation: false });
        const dialogInstance = dialogRef.componentInstance;
        await firstValueFrom(dialogRef.afterClosed());
        if (!dialogInstance.reviewSubmitted) {
            group.ids.forEach((id) => {
                this.userClientService.dismissPendingReview(id).subscribe({ error: () => { } });
            });
        }
    }

    private clearPendingReviewsFlag(): void {
        const userDetails = this.authService.userDetails;
        if (!userDetails?.studentDetails) {
            return;
        }
        this.authService.userDetails = {
            ...userDetails,
            studentDetails: { ...userDetails.studentDetails, hasPendingReviews: false }
        };
    }

    getTrustPilotUrl() {
        return this.apiService.get('api/tools/trustpilot/invitations');
    }

    private isSessionEnded(endTime: string): boolean {
        const appointmentEndDate = new Date(endTime);
        const now = new Date();
        return now >= appointmentEndDate;
    }
}
